import { useCallback, useMemo } from 'react';
import moment, { Moment } from 'moment';
import { useSelector } from 'react-redux';
import { selectCacheDates } from 'store/selectors/app-selectors';
import { RangeValue } from 'ui-components/date-picker/date-types/RangeValue';
import { COMPARE_BAR_SEGMENTS } from 'features/insights/components/filters/date-filter/consts';
import { useFilterDefaultDates } from 'features/insights/hooks/use-filter-description/use-desktop-filter-description';

export const useRelativeSegmentsToDates = () => {
    const datePresets = useSelector(selectCacheDates);
    return useMemo(() => {
        if (!datePresets) {
            return COMPARE_BAR_SEGMENTS;
        }

        return COMPARE_BAR_SEGMENTS.map((segment) => {
            const preset = datePresets[segment.key];
            if (!preset) {
                return segment;
            }
            return {
                ...segment,
                values: [moment(preset.start), moment(preset.end)] as RangeValue<Moment>,
            };
        });
    }, [datePresets]);
};

export const useDateRangeToPreset = () => {
    const defaultDates = useFilterDefaultDates();

    return useCallback(
        ({
            date_start,
            date_end,
            date_label,
        }: {
            date_start: string;
            date_end: string;
            date_label: string;
        }) => {
            const date = Object.values(defaultDates || {}).find(
                (date) =>
                    date.start === date_start &&
                    date.end === date_end &&
                    (date_label ? date.key === date_label : true),
            );

            return date;
        },
        [defaultDates],
    );
};

export const useDateRangeToDatePresetSection = () => {
    const defaultDates = useFilterDefaultDates();

    return useCallback(
        ({
            date_start,
            date_end,
            date_label,
        }: {
            date_start: string;
            date_end: string;
            date_label: string;
        }) => {
            const date = Object.values(defaultDates || {}).find(
                (date) =>
                    date.start === date_start &&
                    date.end === date_end &&
                    (date_label ? date.chosenLabel === date_label : true),
            );

            if (!date) return 'date-range';
            const date_key = date.key.toLowerCase();

            if (
                date_key.includes('last') ||
                date_key.includes('past') ||
                date_key.includes('to_date')
            ) {
                return 'relative';
            } else {
                return 'fixed';
            }
        },
        [defaultDates],
    );
};
